import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { getTargetGroups } from "../../../actions/org/OrgTargetGroupActions";

// Constants
import { defaultPagination } from "../../../constants/UrlConstants";

// Components
import {
  MediaDimension,
  MediaTypeAndLitStatus,
} from "../../../components/campaign-media-form/CampaignMediaFormUtils";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import { ButtonWithLoader } from "../button/Button";

// CSS
import "./MediaInfo.scss";

// Component Sections
export function MediaSiteTitle({ title, setTitle }) {
  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">{"Media Site Title"}</label>
      <div className="col-9">
        <input
          type="search"
          className={"form-control shadow-none rounded-lg"}
          placeholder={"Enter site name. eg Indira Nager Junction facing Metro"}
          value={title}
          onChange={({ target }) => setTitle(target.value)}
        />
      </div>
    </div>
  );
}

export function MediaTiltAngle({ tiltAngle, setTiltAngle }) {
  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">{"Tilt angle to Road"}</label>
      <div className="col-3">
        <input
          className={`form-control shadow-none rounded-lg`}
          placeholder={"Degrees to North (0)"}
          value={tiltAngle}
          onChange={({ target }) => setTiltAngle(target.value)}
        />
      </div>
    </div>
  );
}

function TargetGroupSelect({ targetGroupId, setTargetGroupId = () => {} }) {
  const dispatch = useDispatch();

  // Selector State
  const tgTitles = useSelector((state) => state.orgTargetGroup.tgTitles) || [];
  const targetGroupLoading = useSelector(
    (state) => state.orgTargetGroup.targetGroupLoading
  );

  const pageNo = defaultPagination.pageNo,
    pageSize = defaultPagination.pageSizeMax;

  useEffect(() => {
    dispatch(getTargetGroups(true, "", pageNo, pageSize));
  }, [dispatch]);

  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">Target Group</label>
      <div className="col-3">
        <PlainDropdown
          items={tgTitles}
          className="btn px-2 border shadow-none rounded-lg"
          dropMenuClassName="dropdown-fixed-height"
          placeHolder="Select"
          onItemSelect={(id) => setTargetGroupId(id)}
          selectedId={targetGroupId}
          disabled={targetGroupLoading}
          isFirstItemSelectable={true}
        />
      </div>
    </div>
  );
}

// Functions
/**
 * this function returns "True/False" to call an api
 */
export function validateForm(mediaInfoBean) {
  // skipped tilt field and taking other fields
  const { tilt, ...otherMediaInfo } = mediaInfoBean;

  // since we have to ignore "tiltAngle" input for showing toaster,
  const isAllInputsFilled = Object.keys(otherMediaInfo).every((key) => {
    // If user doesn't fill all the inputs toaster will shown
    if (!mediaInfoBean[key]) {
      toast.error("Please fill all the fields");
      return false;
    }
    return mediaInfoBean[key];
  });

  return isAllInputsFilled;
}

/**
 * Media Site Details Form Component
 */
function SiteDetailsFormSection({
  className = "col-12",
  buttonLabel = "Submit",
  coordinate,
  dispatchFunction = () => {},
  isLoading = false,
  media = {},
  showTgSelect = false,
}) {
  // If we pass Default Media then we show Default values from MEDIA.
  // States
  const [title, setTitle] = useState(media.title || "");
  const [type, setType] = useState(media.type || "");
  const [litStatusStr, setLitStatusStr] = useState(media.litStatus || "");
  const [width, setWidth] = useState(media.width || "");
  const [height, setHeight] = useState(media.height || "");
  const [elevation, setElevation] = useState(media.elevation || "");
  const [tiltAngle, setTiltAngle] = useState(media.tilt || "");
  const [targetGroupId, setTargetGroupId] = useState("");

  // getting lat-lng
  const [latitude, longitude] = coordinate;

  // to pass in Media-Dimension Section
  const mediaDimensionValuesAndFunction = {
    width,
    height,
    elevation,
    setWidth,
    setHeight,
    setElevation,
  };
  const mediaInfoBean = {
    title,
    type,
    lit: litStatusStr,
    width,
    height,
    elevation,
    latitude,
    longitude,
    tilt: tiltAngle,
  };

  // adding tg to bean conditionally
  if (showTgSelect && targetGroupId) {
    mediaInfoBean.targetGroupId = targetGroupId;
  }

  // Tool Action Button Function
  function onClick() {
    // Checking whether all the fields are filled
    const isAllInputsFilled = validateForm(mediaInfoBean);
    if (isAllInputsFilled) {
      dispatchFunction(mediaInfoBean, latitude, longitude);
    }
  }

  return (
    <div className={className}>
      <MediaSiteTitle title={title} setTitle={setTitle} />
      <MediaTypeAndLitStatus
        type={type}
        setType={setType}
        litStatusStr={litStatusStr}
        setLitStatusStr={setLitStatusStr}
        className="rounded-lg"
      />
      <MediaDimension
        mediaDimensionValuesAndFunction={mediaDimensionValuesAndFunction}
        className="rounded-lg"
      />
      {/* temporarily skipping in validation tilt angle field*/}
      <div>
        <MediaTiltAngle tiltAngle={tiltAngle} setTiltAngle={setTiltAngle} />
      </div>

      {/* target group */}
      {showTgSelect && (
        <TargetGroupSelect
          targetGroupId={targetGroupId}
          setTargetGroupId={setTargetGroupId}
        />
      )}

      <div className="report-generate-btn">
        <ButtonWithLoader
          displayContent={buttonLabel}
          onClickFunction={onClick}
          loader={isLoading}
          isDisabled={isLoading}
        />
      </div>
    </div>
  );
}

export default SiteDetailsFormSection;
